'use client';

import type { ClassValue } from 'clsx';
import type { Dispatch, SetStateAction } from 'react';
import { clsxm } from '@vs-shop/utils';
import { CartQuantityDelta } from '@vs-shop/commerce-ui/cartDrawer';
import { captureCause } from '../../../shared/utils/sentry';
import {
  canDecrementQuantity,
  canIncrementQuantity,
  getAvailableQuantityWithCartInMind,
  validateQuantity,
} from '../../../shared/utils/stockValidation';
import { Array, Cause, Exit, Match, Option } from 'effect';
import { useEffect, useRef, useTransition } from 'react';
import { useIntl } from 'react-intl';
import { useDebounceValue } from 'usehooks-ts';

import {
  ProductAmountCounter,
  ProductAmountCounterVariant,
} from '@vs-shop/ui/productAmountCounter';
import { showErrorToast } from '@vs-shop/ui/toast';
import { CartError } from '@vs-shop/engine/capability/cart/errors';

import { cartDomainQuantityInCartByProductVariantCodeAtom } from '../../../effect/bind/order';
import type { CartMutationItem } from '../../../effect/bind/cartMutationItem';
import { cartVariantStatusAtom } from '../../../effect/atom/order/cartMutations';
import { updateOrderItemQuantityAtomFn } from '../../../effect/atom/order/orderMutations';
import { useAtomSet, useAtomValue } from '@effect/atom-react';

type Props = {
  amount: number;
  setAmountAction: Dispatch<SetStateAction<number>>;
  mutation: CartMutationItem;
  className?: ClassValue;
};

export default function ProductAmountCounterCart({
  className,
  amount,
  setAmountAction,
  mutation,
}: Props) {
  const intl = useIntl();
  const cartItemId = mutation.id;
  const productVariantCode = mutation.event.variantCode;

  const hasUserInteractedRef = useRef(false);
  const cartItemSnapshotRef = useRef(mutation);
  const confirmedQuantityRef = useRef(mutation.quantity);
  useEffect(() => {
    confirmedQuantityRef.current = mutation.quantity;
  }, [mutation.quantity]);
  const variantQuantityInCart = useAtomValue(
    cartDomainQuantityInCartByProductVariantCodeAtom(productVariantCode)
  );
  // The counter sends an absolute quantity. While an add of the same variant
  // is pending, that quantity would overwrite the add, so the counter waits.
  const { isAdding } = useAtomValue(cartVariantStatusAtom(productVariantCode));

  const updateItemQuantity = useAtomSet(updateOrderItemQuantityAtomFn(cartItemId), {
    mode: 'promiseExit',
  });

  const [isPending, startTransition] = useTransition();

  const [debouncedAmount] = useDebounceValue(amount, 200);

  const maxOrderable = mutation.maxOrderable;
  // The typed value replaces this line's quantity, so only the other lines of
  // the variant count against the stock.
  const lineCeiling = getAvailableQuantityWithCartInMind(
    maxOrderable,
    variantQuantityInCart - mutation.quantity
  );

  // Effect to update server when debounced amount changes
  useEffect(() => {
    if (!hasUserInteractedRef.current) return;
    // The cart already holds this quantity, for example after the line
    // followed a confirmed change. Sending it again would change nothing.
    if (debouncedAmount === confirmedQuantityRef.current) return;

    startTransition(async () => {
      const result = await updateItemQuantity({
        quantity: debouncedAmount,
        cartItem: cartItemSnapshotRef.current,
        previousQuantity: cartItemSnapshotRef.current.quantity,
      });

      Exit.match(result, {
        onFailure: (cause) => {
          captureCause(cause, {
            component: 'ProductAmountCounterCart',
            action: 'Update item quantity failed',
            additionalData: {
              cartItemId,
              productVariantCode,
              quantity: debouncedAmount,
              previousQuantity: cartItemSnapshotRef.current.quantity,
            },
          });
          const failReason = cause.reasons.find(Cause.isFailReason);

          if (failReason) {
            Match.value(failReason.error).pipe(
              Match.tag(CartError.Tags.Rejected, (error) => {
                showErrorToast(Option.getOrElse(Array.head(error.messages), () => error.message));
              }),
              Match.orElse(() => {
                showErrorToast(
                  intl.formatMessage({
                    id: 'error.orderMutation.generic',
                    defaultMessage: 'Došlo k chybě při zpracování objednávky.',
                  })
                );
              })
            );
          }
        },
        onSuccess: () => {
          // GTM events are handled in the flow
        },
      });
    });
  }, [cartItemId, productVariantCode, debouncedAmount, updateItemQuantity, intl]);

  const onDecrement = () => {
    if (isAdding) return;
    const newAmount = amount > 1 ? amount - 1 : amount;
    if (newAmount !== amount) {
      hasUserInteractedRef.current = true;
      cartItemSnapshotRef.current = mutation;
      setAmountAction(newAmount);
    }
  };

  const onIncrement = () => {
    if (!isAdding && canIncrementQuantity(maxOrderable, variantQuantityInCart)) {
      const newAmount = amount + 1;
      hasUserInteractedRef.current = true;
      cartItemSnapshotRef.current = mutation;
      setAmountAction(newAmount);
    }
  };

  const handleInputChange = (value: string) => {
    if (isAdding) return;
    const validatedQuantity = validateQuantity(value, lineCeiling);

    if (validatedQuantity > 0) {
      hasUserInteractedRef.current = true;
      cartItemSnapshotRef.current = mutation;
      setAmountAction(validatedQuantity);
    }
  };

  const locked = isPending || isAdding;
  const quantityDelta = amount - mutation.quantity;
  const decrementDisabled = locked || !canDecrementQuantity(amount);
  const incrementDisabled = locked || !canIncrementQuantity(maxOrderable, amount);

  return (
    <div className="flex items-center gap-2" data-quantity-pending={quantityDelta !== 0}>
      <ProductAmountCounter
        amount={amount}
        onDecrementAction={onDecrement}
        onIncrementAction={onIncrement}
        onInputChangeAction={handleInputChange}
        isLoading={false}
        decrementDisabled={decrementDisabled}
        incrementDisabled={incrementDisabled}
        decrementLabel={intl.formatMessage({
          id: 'component.client.product.productAmountCounter.decrementLabel',
          defaultMessage: 'Snížit počet',
        })}
        incrementLabel={intl.formatMessage({
          id: 'component.client.product.productAmountCounter.incrementLabel',
          defaultMessage: 'Zvýšit počet',
        })}
        amountLabel={intl.formatMessage({
          id: 'component.client.product.productAmountCounter.amountLabel',
          defaultMessage: 'Počet',
        })}
        variant={ProductAmountCounterVariant.TALL}
        className={clsxm(className)}
      />
      <CartQuantityDelta
        delta={quantityDelta}
        aria-label={intl.formatNumber(quantityDelta, { signDisplay: 'always' })}
      />
    </div>
  );
}
